<script lang="ts" setup>
import { StateInput } from '@/components/common/inputs/state-input';
import {
  PhCircleNotch,
  PhFloppyDisk,
  PhPencilSimple,
  PhTrash,
  PhX,
} from '@phosphor-icons/vue';
import { computed } from 'vue';

type ComponentState = 'hidden' | 'visible' | 'disabled';
type InputState = 'edit' | 'read-only' | 'hidden';

export interface SectionCardProps {
  name: string;
  isEditing: boolean;
  loading?: boolean;
  componentState?: {
    edit?: ComponentState;
    title?: InputState;
    delete?: ComponentState;
    save?: ComponentState;
  };
}

type SectionCardToggleEditEvent = { type: 'toggle-edit' };
type SectionCardCancelEvent = { type: 'cancel' };
type SectionCardUpdateNameEvent = { type: 'update-name'; value: string };
type SectionCardDeleteEvent = { type: 'delete' };

export type SectionCardActionEvent =
  | SectionCardToggleEditEvent
  | SectionCardCancelEvent
  | SectionCardUpdateNameEvent
  | SectionCardDeleteEvent;

const props = defineProps<SectionCardProps>();

const emit = defineEmits<{
  action: [event: SectionCardActionEvent];
}>();

const inputMode = computed<'edit' | 'read-only' | 'hidden'>(() =>
  props.isEditing ? props.componentState?.title || 'edit' : 'read-only',
);
const editButtonState = computed<ComponentState>(
  () => props.componentState?.edit || 'hidden',
);
const saveButtonState = computed<ComponentState>(
  () => props.componentState?.save || 'visible',
);
const deleteButtonState = computed<ComponentState>(
  () => props.componentState?.delete || 'hidden',
);
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">
        <StateInput
          :mode="inputMode"
          class="inline-input header-name-input"
          :model-value="props.name"
          @update:model-value="
            emit('action', { type: 'update-name', value: $event })
          "
        />
      </p>
      <button
        v-if="props.isEditing"
        class="header-btn cancel-btn"
        @click="emit('action', { type: 'cancel' })"
      >
        <PhX :size="15" />
        Avbryt
      </button>
      <button
        v-if="editButtonState !== 'hidden'"
        class="header-btn edit-btn"
        :class="{ 'is-active': props.isEditing }"
        @click="emit('action', { type: 'toggle-edit' })"
        :disabled="
          props.loading ||
          (props.isEditing
            ? saveButtonState === 'disabled'
            : editButtonState === 'disabled')
        "
      >
        <PhCircleNotch
          v-if="props.loading && !props.isEditing"
          :size="15"
          class="spin"
        />
        <PhFloppyDisk v-else-if="props.isEditing" :size="15" />
        <PhPencilSimple v-else :size="15" />
        {{ props.isEditing ? 'Spara' : 'Redigera' }}
      </button>
      <button
        v-if="deleteButtonState !== 'hidden'"
        class="header-btn delete-btn"
        @click="emit('action', { type: 'delete' })"
        :disabled="deleteButtonState === 'disabled'"
      >
        <PhTrash :size="15" />
      </button>
    </div>
    <div class="card-content section-card-content p-0">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.card {
  overflow: hidden;
}

.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
}

.header-btn {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  height: auto;
  width: auto;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.edit-btn.is-active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.cancel-btn {
  margin-right: 0.4rem;
}

.cancel-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.delete-btn {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
  padding: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background: #fca5a5;
  border-color: #f87171;
  color: #dc2626;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 0.8s linear infinite;
}

.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.header-name-input {
  font-size: inherit;
  font-weight: inherit;
  flex: 1;
  min-width: 0;
}
</style>
